import { useCallback, useEffect, useState } from "react";

import {
  useAPI,
  useAuthContext,
  useInterval,
  useWriterComponentState,
} from "hooks";
import { extractAPIRequestErrorMessage } from "utils";

import { NotificationModal } from "./NotificationModal";
import { useNotificationContext } from "../../../../context/NotificationContext";
import {
  isNotificationUnread,
  notificationIsRenderable,
} from "../NotificationCard/content";

import type {
  NotificationsResponse,
  NotificationUpdateParams,
  UpdateNotificationsBody,
} from "./types";

const DEFAULT_NOTIFICATIONS_POLL_INTERVAL_MS = 30000;
// A build without NOTIFICATION_POLL_INTERVAL_MS in the shell env expands the
// .env.production placeholder to "" and Number("") === 0, so guard with > 0.
const configuredPollIntervalMilliseconds = Number(
  import.meta.env.REACT_APP_NOTIFICATION_POLL_INTERVAL_MS,
);
const NOTIFICATIONS_POLL_INTERVAL_MS =
  Number.isFinite(configuredPollIntervalMilliseconds) &&
  configuredPollIntervalMilliseconds > 0
    ? configuredPollIntervalMilliseconds
    : DEFAULT_NOTIFICATIONS_POLL_INTERVAL_MS;

interface Props {
  open: boolean;
  onClose: VoidFunction;
  onUnreadNotificationAmountChanged: (amount: number) => void;
}

export function NotificationModalContainer(props: Props) {
  const { open, onClose, onUnreadNotificationAmountChanged } = props;
  const {
    notifications,
    updateNotifications,
    loading,
    setLoading: setContextLoading,
  } = useNotificationContext();
  const [error, setError] = useState<null | string>(null);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const { api } = useAPI();
  const { user } = useAuthContext();

  const notificationIsUnread = (notification: Notification) =>
    isNotificationUnread(notification) &&
    notification.deleted_at == null &&
    notificationIsRenderable(notification);

  const getUnreadNotificationAmount = useCallback(
    (fetchedNotifications: Notification[]) =>
      fetchedNotifications.filter((notification) =>
        notificationIsUnread(notification),
      ).length,
    [],
  );

  // The backend caps the list at the 100 most recent, ordered unread first,
  // so the derived unread amount is exact until the badge saturates at 99+.
  const fetchNotifications = useCallback(() => {
    if (user) {
      api<NotificationsResponse>("get", "/notifications")
        .then((response) => {
          updateNotifications(response.data.results); // Update context instead of local state
          setError(null);
          onUnreadNotificationAmountChanged(
            getUnreadNotificationAmount(response.data.results),
          );
        })
        .catch((e) => {
          setError(
            extractAPIRequestErrorMessage(e, "Failed to fetch notifications."),
          );
        })
        .finally(() => {
          setContextLoading(false);
        });
    }
  }, [
    api,
    user,
    getUnreadNotificationAmount,
    onUnreadNotificationAmountChanged,
    updateNotifications,
    setContextLoading,
  ]);

  useInterval(fetchNotifications, NOTIFICATIONS_POLL_INTERVAL_MS);

  useEffect(() => {
    if (loading) {
      fetchNotifications();
    }
  }, [loading, fetchNotifications]);

  useEffect(() => {
    if (open) {
      setContextLoading(true);
    }
  }, [open, setContextLoading]);

  const markAllAsReadWriter = useWriterComponentState({
    errorMessage: "Failed to mark notifications as read.",
    writeConfig: { method: "patch", url: "/notifications/mark_all_read" },
  });

  const deleteAllWriter = useWriterComponentState({
    errorMessage: "Failed to delete notifications.",
    writeConfig: { method: "patch", url: "/notifications/delete_all" },
  });

  // Clearing both writers here makes any successful action dismiss a stale
  // error left behind by a previously failed bulk action.
  const refreshNotifications = useCallback(() => {
    markAllAsReadWriter.resetState();
    deleteAllWriter.resetState();
    setContextLoading(true); // re-fetch notifications (and the derived badge count)
  }, [markAllAsReadWriter, deleteAllWriter, setContextLoading]);

  const markAllAsRead = (): void => {
    markAllAsReadWriter.write(refreshNotifications);
  };

  const deleteAll = (): void => {
    deleteAllWriter.write(refreshNotifications);
  };

  const buildUpdateParamsObject = (
    notification: Notification,
    read: boolean,
    delete_notification: boolean,
  ): NotificationUpdateParams => ({
    id: notification.id,
    read,
    delete: delete_notification,
  });

  const getRequestBody = (
    paramsList: NotificationUpdateParams[],
  ): UpdateNotificationsBody => ({
    notifications: paramsList,
  });

  const postNotificationUpdates = async (
    requestBody: UpdateNotificationsBody,
    fallbackErrorMsg: string,
  ): Promise<void> => {
    setIsUpdating(true);
    return api<unknown>("patch", "/notifications/update_many", requestBody)
      .then(() => {
        setError(null);
        refreshNotifications();
      })
      .catch((e) => {
        setError(extractAPIRequestErrorMessage(e, fallbackErrorMsg));
      })
      .finally(() => setIsUpdating(false));
  };

  const markNotifictionAsRead = async (
    notification: Notification,
  ): Promise<void> => {
    const requestBody = getRequestBody([
      buildUpdateParamsObject(notification, true, false),
    ]);
    return postNotificationUpdates(
      requestBody,
      "Failed to mark notification as read",
    );
  };

  const markNotifictionAsDeleted = (notification: Notification): void => {
    const requestBody = getRequestBody([
      buildUpdateParamsObject(notification, false, true),
    ]);
    postNotificationUpdates(
      requestBody,
      "Failed to mark notification as deleted",
    );
  };

  return (
    <NotificationModal
      open={open}
      onClose={onClose}
      notifications={notifications}
      loading={loading}
      error={error ?? markAllAsReadWriter.error ?? deleteAllWriter.error}
      onMarkAllAsRead={markAllAsRead}
      onDeleteAll={deleteAll}
      onMarkNotifictionAsRead={markNotifictionAsRead}
      onMarkNotifictionAsDeleted={markNotifictionAsDeleted}
      isUpdating={
        isUpdating || markAllAsReadWriter.loading || deleteAllWriter.loading
      }
    />
  );
}
